'use client';

import { useInView } from 'react-intersection-observer';
import { Fragment, MouseEvent, useEffect, useRef, useState } from 'react';
import { useDebounce } from '@uidotdev/usehooks';
import classNames from 'classnames';
import { BrandListingItemViewModel } from '@/types/api';
import { getBaseURL } from '@/features/navigation/utils/url-helpers';
import { AppNextLink } from '@/features/app-next-link';
import { TextField } from '@/features/form';
import { useGetMenuBrands } from '@/components/header/utils/use-fetch-brands';
import { IconClose, IconSearchNormal } from '@/components/svg-symbols/icons';
import { PLPPages } from '@/app/(plp)/types';
import SearchBrandResult from './search-brand-result';
import TopBrands from './top-brands';
import { useCategoryModal } from '../../category-modal/context/category-modal-context';
import getBrandAlphabet from '../../utils/get-brand-alphabet';
import SearchSkeleton from '../search-skeleton';
import Title from '../title';
interface Props {
  type: 'mobile' | 'desktop';
  navigateToBrand: (itemHref: string) => void;
}
function AllBrands({
  type,
  navigateToBrand
}: Props) {
  const [query, setQuery] = useState('');
  const {
    ref,
    inView
  } = useInView();
  const {
    ref: searchBrandRef,
    inView: inViewBrand
  } = useInView();
  const brandsRef = useRef<BrandListingItemViewModel[]>([]);
  const {
    isOpen: isCategoryMobileOpen,
    close: closeCategoryModal
  } = useCategoryModal();
  const debouncedQuery = useDebounce(query, 300);
  const onSearchBoxChangeHandler = (value: string) => {
    setQuery(value);
  };
  const shouldFetchBrands = type === 'desktop' || isCategoryMobileOpen;
  const {
    fetchNextPage,
    filteredBrands,
    isFetching,
    lastPageNumber,
    pageCount
  } = useGetMenuBrands(debouncedQuery, shouldFetchBrands);
  useEffect(() => {
    // when the user scroll the carousel
    if (!isFetching && inView && lastPageNumber < pageCount) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView, isFetching, lastPageNumber, pageCount]);
  useEffect(() => {
    if (inViewBrand || !query.length) return;
    setQuery('');
  }, [inViewBrand, query]);
  useEffect(() => {
    if (query) return;
    brandsRef.current = filteredBrands;
  }, [query, filteredBrands]);
  const handleClickItem = (e: MouseEvent, itemHref: string) => {
    e.preventDefault();
    closeCategoryModal();
    navigateToBrand(itemHref);
  };
  return <div className={classNames('flex flex-col gap-0', {
    'min-h-0': type === 'desktop'
  })} ref={searchBrandRef} data-sentry-component="AllBrands" data-sentry-source-file="all-brands.tsx">
            {type === 'desktop' && <form toolname='brand-search' tooldescription='search in all brands' className='mt-1 shrink-0 md:mb-4 md:mt-0 md:min-w-[363px]'>
                    <TextField value={query} onChange={e => {
        onSearchBoxChangeHandler(e.target.value.trimStart());
      }} placeholder='جست‌وجوی برند' inputSize='large' id='brand-search' rightIcon={!query ? <IconSearchNormal className='h-6 w-6 !text-text-mediumGray' /> : <IconClose className='h-6 w-6 cursor-pointer !text-text-mediumGray' onClick={() => setQuery('')} />} spaceBottomStyle='hidden' className='md:ml-3' name='brand-search' toolparamdescription='search in brands query' />
                </form>}
            <div className='md:min-h-0 md:flex-1 md:overflow-y-auto'>
                {isFetching && type === 'desktop' ? <SearchSkeleton className='mt-2 lg:mt-0' /> : <>
                        {!filteredBrands.length && !!query.length && <div className='mb-8 mt-6 text-body-14 font-normal text-text-mediumGray lg:mt-0'>
                                برای جست‌وجوی شما نتیجه‌ای یافته نشد!
                            </div>}

                        {!!filteredBrands.length && query.length > 1 && <SearchBrandResult brands={filteredBrands} />}
                        <TopBrands mode='mobile' navigateToBrand={navigateToBrand} />
                    </>}

                <div className={classNames('hidden-scrollbr mt-4 border-border-gray-medium md:mt-0 md:border-l', {
        'hidden lg:block': isFetching && query.length
      })}>
                    <Title text='همه برندها (ا - ی)' data-sentry-element="Title" data-sentry-source-file="all-brands.tsx" />

                    <ul className='mt-2 pr-2 text-body-14 font-normal md:mt-4 md:pr-2.5'>
                        {brandsRef.current.map(({
            id,
            slug,
            nameFa,
            nameEn
          }, index) => {
            const brandAlphabet = getBrandAlphabet(nameFa || '', brandsRef.current[index - 1]?.nameFa);
            const itemHref = `/${getBaseURL({
              page: PLPPages.Brands,
              slug: slug || ''
            })}`;
            return <Fragment key={id}>
                                    {brandAlphabet.alphabet && <span id={`alphabet-${brandAlphabet.index}`} className='mb-1 mt-2 block w-11 rounded-l-sm bg-brand-secondary text-center leading-6 text-white'>
                                            {brandAlphabet.alphabet}
                                        </span>}

                                    <li>
                                        <AppNextLink href={itemHref} onClick={e => handleClickItem(e, itemHref)} className='block py-1 text-body-14 font-normal leading-6 hover:text-brand-secondary' prefetch={false}>
                                            <span className='text-text-darkGray'>{`${nameFa} `}</span>
                                            <span className='text-text-mediumGray'>({nameEn})</span>
                                        </AppNextLink>
                                    </li>
                                </Fragment>;
          })}
                        {!isFetching && <div className='h-5 w-full' ref={ref} />}
                    </ul>
                </div>
            </div>
        </div>;
}
export default AllBrands;